import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Gift, Users, Copy, Share2, CheckCircle, Mail, Twitter, Facebook, Linkedin } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import api from '../services/api';

const Referral = () => {
  const { user } = useAuth();
  const [referralCode, setReferralCode] = useState('');
  const [referralStats, setReferralStats] = useState({ total: 0, successful: 0, pending: 0, earnings: 0 });
  const [referrals, setReferrals] = useState([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    try {
      const response = await api.get('/referral/stats');
      setReferralCode(response.data.referral_code || generateReferralCode());
      setReferralStats(response.data.stats || { total: 0, successful: 0, pending: 0, earnings: 0 });
      setReferrals(response.data.referrals || []);
    } catch (error) {
      setReferralCode(generateReferralCode());
    }
  };

  const generateReferralCode = () => {
    const name = user?.name?.split(' ')[0]?.toUpperCase() || 'USER';
    return `${name}${Math.random().toString(36).substring(2, 6).toUpperCase()}`;
  };

  const referralLink = `https://glucose-buddy-28.preview.emergentagent.com/signup?ref=${referralCode}`;

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast({ title: 'Copied!', description: 'Referral link copied to clipboard' });
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to copy', variant: 'destructive' });
    }
  };

  const shareVia = (platform) => {
    const text = "I'm using DiaWise to manage my diabetes. Join me and get 1 month Pro free!";
    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(referralLink)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(referralLink)}`,
      email: `mailto:?subject=Join me on DiaWise&body=${encodeURIComponent(text + '\n\n' + referralLink)}`
    };
    window.open(urls[platform], '_blank');
  };

  const rewards = [
    { referrals: 1, reward: '1 Week Pro Free', icon: '🎁' },
    { referrals: 3, reward: '1 Month Pro Free', icon: '⭐' },
    { referrals: 5, reward: '3 Months Pro Free', icon: '🏆' },
    { referrals: 10, reward: '1 Year Pro Free', icon: '👑' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Invite Friends, Get Rewards</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">Share DiaWise and earn free Pro membership</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-cyan-500 text-white">
          <CardContent className="p-4 text-center">
            <Users className="w-8 h-8 mx-auto mb-2 opacity-80" />
            <p className="text-3xl font-bold">{referralStats.total}</p>
            <p className="text-blue-100 text-sm">Total Invites</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-500 to-emerald-500 text-white">
          <CardContent className="p-4 text-center">
            <CheckCircle className="w-8 h-8 mx-auto mb-2 opacity-80" />
            <p className="text-3xl font-bold">{referralStats.successful}</p>
            <p className="text-green-100 text-sm">Successful</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-yellow-500 to-orange-500 text-white">
          <CardContent className="p-4 text-center">
            <Gift className="w-8 h-8 mx-auto mb-2 opacity-80" />
            <p className="text-3xl font-bold">{referralStats.pending}</p>
            <p className="text-yellow-100 text-sm">Pending</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-500 to-pink-500 text-white">
          <CardContent className="p-4 text-center">
            <Gift className="w-8 h-8 mx-auto mb-2 opacity-80" />
            <p className="text-3xl font-bold">${referralStats.earnings}</p>
            <p className="text-purple-100 text-sm">Value Earned</p>
          </CardContent>
        </Card>
      </div>

      {/* Referral Link */}
      <Card className="dark:bg-gray-800">
        <CardHeader>
          <CardTitle className="dark:text-white">Your Referral Link</CardTitle>
          <CardDescription>Share this link with friends to earn rewards</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input
              value={referralLink}
              readOnly
              className="font-mono text-sm"
            />
            <Button onClick={copyToClipboard} className="shrink-0">
              {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
          
          <div className="flex gap-2 justify-center">
            <Button variant="outline" size="sm" onClick={() => shareVia('twitter')} className="flex-1">
              <Twitter className="w-4 h-4 mr-2" /> Twitter
            </Button>
            <Button variant="outline" size="sm" onClick={() => shareVia('facebook')} className="flex-1">
              <Facebook className="w-4 h-4 mr-2" /> Facebook
            </Button>
            <Button variant="outline" size="sm" onClick={() => shareVia('linkedin')} className="flex-1">
              <Linkedin className="w-4 h-4 mr-2" /> LinkedIn
            </Button>
            <Button variant="outline" size="sm" onClick={() => shareVia('email')} className="flex-1">
              <Mail className="w-4 h-4 mr-2" /> Email
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Rewards Tiers */}
      <Card className="dark:bg-gray-800">
        <CardHeader>
          <CardTitle className="dark:text-white">🎁 Referral Rewards</CardTitle>
          <CardDescription>The more friends you invite, the bigger the rewards</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {rewards.map((tier, index) => {
              const achieved = referralStats.successful >= tier.referrals;
              return (
                <div
                  key={index}
                  className={`p-4 rounded-xl border-2 text-center transition-all ${
                    achieved
                      ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                      : 'border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <div className="text-3xl mb-2">{tier.icon}</div>
                  <p className="font-bold text-gray-900 dark:text-white">{tier.referrals} Friends</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{tier.reward}</p>
                  {achieved && (
                    <Badge className="mt-2 bg-green-100 text-green-700">Unlocked!</Badge>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* How It Works */}
      <Card className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white">
        <CardContent className="p-6">
          <h3 className="text-xl font-bold mb-4">How It Works</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-2">
                <Share2 className="w-6 h-6" />
              </div>
              <p className="font-semibold">1. Share Your Link</p>
              <p className="text-emerald-100 text-sm">Send your unique link to friends</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-2">
                <Users className="w-6 h-6" />
              </div>
              <p className="font-semibold">2. Friends Sign Up</p>
              <p className="text-emerald-100 text-sm">They create a free account</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-2">
                <Gift className="w-6 h-6" />
              </div>
              <p className="font-semibold">3. Both Get Rewarded</p>
              <p className="text-emerald-100 text-sm">You both get Pro features free</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Referral;
